import { useState, useEffect } from 'react';
import {
  Stack, Title, Text, SegmentedControl, NumberInput,
  Button, Group, Paper, Table, Badge, ScrollArea, Divider, Modal, Select, Alert, Loader
} from '@mantine/core';
import { IconFileImport, IconInfoCircle } from '@tabler/icons-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useGetImportSummary } from '../../../../hooks/useGetImportSummary';
import { useGetHourlyData } from '../../../../hooks/useGetHourlyData';
import type { HourlyConfig } from '../../../../types/calculator';
import { MONTH_NAMES_PL } from '../../../../types/calculator';

/**
 * HourlyConsumptionConfig Component Props
 */
interface HourlyConsumptionConfigProps {
  value: HourlyConfig;
  onChange: (config: HourlyConfig) => void;
  householdSize: number | '5+';
  hasHeatPump: boolean;
  hasSolar: boolean;
  hasEV: boolean;
  monthlyConsumption?: number[];  // NEW - array of 12 monthly values
}

/**
 * HourlyConsumptionConfig Component
 *
 * Allows users to configure hourly consumption patterns separately for:
 * - Weekdays (Monday-Friday)
 * - Weekends (Saturday-Sunday)
 *
 * Features:
 * - 24 hourly inputs organized by time of day (Night, Morning, Afternoon, Evening)
 * - Quick pattern buttons (Uniform, Morning & Evening peaks, Work from Home, Suggested)
 * - Copy weekday → weekend helper
 * - Validation (sum must equal 100%)
 */
export function HourlyConsumptionConfig({
  value,
  onChange,
  householdSize,
  hasHeatPump,
  hasSolar,
  hasEV,
  monthlyConsumption
}: HourlyConsumptionConfigProps) {
  const { isAuthenticated } = useAuth();
  const [activeDay, setActiveDay] = useState<'weekday' | 'weekend'>('weekday');
  const [tauronModalOpened, setTauronModalOpened] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [loadingTauronData, setLoadingTauronData] = useState(false);

  // Check if user has imported data for current year
  const currentYear = new Date().getFullYear();
  const { data: importSummary, loading: summaryLoading } = useGetImportSummary(currentYear);
  const { getHourlyData, loading: hourlyDataLoading } = useGetHourlyData();

  // Check if user has any imported (non-extrapolated) months
  const hasImportedData = importSummary && importSummary.imports.some(imp => !imp.isExtrapolated);

  // CRITICAL: Migrate old decimal values (0-1) to percentage (0-100) on component mount
  useEffect(() => {
    // Check if values are in old decimal format (all values < 2 indicates decimal)
    const isWeekdayDecimal = value.weekday.every(v => v < 2);
    const isWeekendDecimal = value.weekend.every(v => v < 2);

    if (isWeekdayDecimal || isWeekendDecimal) {
      onChange({
        weekday: isWeekdayDecimal ? value.weekday.map(v => v * 100) : value.weekday,
        weekend: isWeekendDecimal ? value.weekend.map(v => v * 100) : value.weekend,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run only once on mount for migration, deps would cause infinite loop
  }, []);

  /**
   * Handle loading hourly data from Tauron import
   * Converts imported hourly consumption to percentage distribution
   */
  const handleLoadTauronData = async () => {
    if (!selectedMonth) return;

    setLoadingTauronData(true);
    try {
      const result = await getHourlyData(selectedMonth);

      if (!result.success || !result.hourlyData) {
        throw new Error('Nie udało się załadować danych godzinowych');
      }

      // Convert hourly consumption data to percentage distribution
      // const _totalDailyConsumption = result.avgDailyConsumption;

      // Group hours by day type (weekday vs weekend)
      const weekdayHours = new Array(24).fill(0);
      const weekendHours = new Array(24).fill(0);
      const weekdayCounts = new Array(24).fill(0);
      const weekendCounts = new Array(24).fill(0);

      result.hourlyData.forEach(point => {
        const date = new Date(point.timestamp);
        const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
        const hour = date.getHours();
        const consumption = point.consumption;

        if (dayOfWeek === 0 || dayOfWeek === 6) {
          // Weekend (Saturday or Sunday)
          weekendHours[hour] += consumption;
          weekendCounts[hour] += 1;
        } else {
          // Weekday (Monday-Friday)
          weekdayHours[hour] += consumption;
          weekdayCounts[hour] += 1;
        }
      });

      // Calculate average consumption per hour for each day type
      const avgWeekdayHours = weekdayHours.map((total, hour) =>
        weekdayCounts[hour] > 0 ? total / weekdayCounts[hour] : 0
      );
      const avgWeekendHours = weekendHours.map((total, hour) =>
        weekendCounts[hour] > 0 ? total / weekendCounts[hour] : 0
      );

      // Convert to percentages (normalize so sum = 100%)
      const weekdaySum = avgWeekdayHours.reduce((sum, val) => sum + val, 0);
      const weekendSum = avgWeekendHours.reduce((sum, val) => sum + val, 0);

      const weekdayPercentages = weekdaySum > 0
        ? avgWeekdayHours.map(val => (val / weekdaySum) * 100)
        : Array(24).fill(100 / 24); // Fallback to uniform if no data

      const weekendPercentages = weekendSum > 0
        ? avgWeekendHours.map(val => (val / weekendSum) * 100)
        : Array(24).fill(100 / 24); // Fallback to uniform if no data

      // Apply to current configuration
      onChange({
        weekday: weekdayPercentages,
        weekend: weekendPercentages,
      });

      setTauronModalOpened(false);
      setSelectedMonth(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Nie udało się załadować danych z raportu Tauron');
    } finally {
      setLoadingTauronData(false);
    }
  };

  // Define time periods for horizontal table layout
  const PERIODS = [
    { name: 'Noc', color: 'blue', startHour: 0, endHour: 5 },
    { name: 'Poranek', color: 'orange', startHour: 6, endHour: 11 },
    { name: 'Popołudnie', color: 'yellow', startHour: 12, endHour: 17 },
    { name: 'Wieczór', color: 'grape', startHour: 18, endHour: 23 }
  ];

  // Calculate kWh for display
  const averageMonthlyKwh = monthlyConsumption
    ? monthlyConsumption.reduce((a, b) => a + b, 0) / 12
    : 292; // Default fallback

  // For uniform pattern, daily consumption should be the same for weekday and weekend
  // Daily consumption = monthly total / 30 days
  const dailyKwh = averageMonthlyKwh / 30;

  const calculateHourlyKwh = (percentage: number): number => {
    return (percentage / 100) * dailyKwh;
  };

  // Helper: Copy weekday to weekend
  const copyWeekdayToWeekend = () => {
    onChange({ ...value, weekend: [...value.weekday] });
  };

  // Helper: Set uniform distribution (4.17% per hour)
  const setUniform = (dayType: 'weekday' | 'weekend') => {
    const uniformValue = 100.0 / 24; // Equal distribution in percentage (4.166%)
    onChange({
      ...value,
      [dayType]: Array(24).fill(uniformValue),
    });
  };

  // Helper: Apply morning & evening peak pattern
  const applyMorningEveningPattern = (dayType: 'weekday' | 'weekend') => {
    // Pattern: Low at night, peaks at 6-9 (morning) and 18-22 (evening)
    const pattern = [
      0.3, 0.3, 0.3, 0.3, 0.4, 0.5, // 00-05: Night (low)
      1.2, 1.5, 1.3, 0.8, 0.7, 0.6, // 06-11: Morning peak
      0.6, 0.7, 0.7, 0.8, 0.9, 1.0, // 12-17: Afternoon
      1.4, 1.6, 1.5, 1.2, 0.9, 0.6, // 18-23: Evening peak
    ];

    // Normalize to sum = 100%
    const sum = pattern.reduce((a, b) => a + b, 0);
    const normalized = pattern.map(v => (v / sum) * 100);

    onChange({
      ...value,
      [dayType]: normalized,
    });
  };

  // Helper: Apply work from home pattern
  const applyWorkFromHomePattern = (dayType: 'weekday' | 'weekend') => {
    // Work from home: high daytime consumption (9-17)
    const pattern = [
      0.3, 0.3, 0.3, 0.3, 0.4, 0.5,  // 00-05: Night (low)
      0.8, 1.0, 1.2, 1.5, 1.6, 1.5,  // 06-11: Morning + work hours
      1.4, 1.3, 1.2, 1.0, 0.9, 0.8,  // 12-17: Lunch + afternoon work
      1.1, 1.2, 1.0, 0.8, 0.6, 0.4,  // 18-23: Evening
    ];

    const sum = pattern.reduce((a, b) => a + b, 0);
    const normalized = pattern.map(v => (v / sum) * 100);

    onChange({
      ...value,
      [dayType]: normalized,
    });
  };

  // Helper: Apply suggested pattern based on household data
  const applySuggestedPattern = (dayType: 'weekday' | 'weekend') => {
    // Start with base pattern (mornings & evenings)
    let pattern = [
      0.3, 0.3, 0.3, 0.3, 0.4, 0.5,  // Night
      1.2, 1.5, 1.3, 0.8, 0.7, 0.6,  // Morning peak
      0.6, 0.7, 0.7, 0.8, 0.9, 1.0,  // Afternoon
      1.4, 1.6, 1.5, 1.2, 0.9, 0.6,  // Evening peak
    ];

    // Modify based on household characteristics
    if (hasHeatPump) {
      // Heat pump: increase morning (6-8) and evening (17-22) consumption
      pattern[6] += 0.5;
      pattern[7] += 0.5;
      pattern[17] += 0.5;
      pattern[18] += 0.5;
      pattern[19] += 0.5;
      pattern[20] += 0.5;
    }

    if (hasSolar) {
      // Solar: reduce daytime consumption (9-17) by 40%
      for (let h = 9; h <= 17; h++) {
        pattern[h] *= 0.6;
      }
    }

    if (hasEV) {
      // EV: increase night consumption (22-06) for charging
      for (let h = 22; h <= 23; h++) {
        pattern[h] += 0.8;
      }
      for (let h = 0; h <= 6; h++) {
        pattern[h] += 0.8;
      }
    }

    // Scale by household size (more people = more evening peak)
    const size = householdSize === '5+' ? 5 : Number(householdSize);
    const sizeMultiplier = 0.8 + (size * 0.1);
    pattern = pattern.map((v, i) => {
      // Apply multiplier more to peak hours (17-22)
      if (i >= 17 && i <= 22) {
        return v * sizeMultiplier;
      }
      return v;
    });

    // Normalize
    const sum = pattern.reduce((a, b) => a + b, 0);
    const normalized = pattern.map(v => (v / sum) * 100);

    onChange({
      ...value,
      [dayType]: normalized,
    });
  };

  // Get current values based on active day type
  const currentValues = activeDay === 'weekday' ? value.weekday : value.weekend;

  // Calculate sum (should be 100%)
  const sum = currentValues.reduce((a, b) => a + b, 0);
  const isValid = Math.abs(sum - 100.0) <= 1.0; // Allow 1% tolerance

  // Calculate total kWh for all 24 hours
  const totalKwh = currentValues.reduce((total, percentage) => {
    const kwhValue = calculateHourlyKwh(percentage); // Already in percentage (0-100)
    return total + kwhValue;
  }, 0);

  // Helper: Normalize values to sum to 100%
  const normalizeValues = () => {
    if (sum === 0) {return;} // Prevent division by zero

    const normalized = currentValues.map(value => (value / sum) * 100);
    onChange({
      ...value,
      [activeDay]: normalized,
    });
  };

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <div>
          <Title order={4}>Krok 3: Konfiguracja godzinowa zużycia</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Określ, jak zużywasz energię w ciągu doby. Wybierz gotowy wzorzec lub dostosuj samodzielnie.
          </Text>
        </div>

        {/* Day type selector */}
        <SegmentedControl
          value={activeDay}
          onChange={(value) => setActiveDay(value as 'weekday' | 'weekend')}
          data={[
            { label: 'Dzień tygodnia (Pn-Pt)', value: 'weekday' },
            { label: 'Weekend (So-Nd)', value: 'weekend' },
          ]}
          fullWidth
        />

        {/* Quick actions - Pattern buttons */}
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={500}>
            Wybierz gotowy wzorzec zużycia (dostosowany do danych z Kroku 2):
          </Text>
          <Group gap="xs" wrap="wrap">
            <Button
              size="xs"
              variant="light"
              onClick={() => setUniform(activeDay)}
            >
              Równomierne (24h)
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => applyMorningEveningPattern(activeDay)}
            >
              Poranki i wieczory
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => applyWorkFromHomePattern(activeDay)}
            >
              Praca w domu
            </Button>

            {/* Specialized patterns based on household data */}
            {hasHeatPump && (
              <Button
                size="xs"
                variant="light"
                color="orange"
                onClick={() => {
                  // Heat pump pattern: high morning (6-9) and evening (17-22)
                  const pattern = [
                    0.2, 0.2, 0.2, 0.2, 0.3, 0.5,  // 00-05: Night (low)
                    1.8, 2.2, 2.0, 1.0, 0.8, 0.7,  // 06-11: Morning peak (heating)
                    0.7, 0.8, 0.8, 0.9, 1.0, 1.5,  // 12-17: Afternoon
                    2.0, 2.3, 2.1, 1.8, 1.2, 0.8,  // 18-23: Evening peak (heating)
                  ];
                  const sum = pattern.reduce((a, b) => a + b, 0);
                  const normalized = pattern.map(v => (v / sum) * 100);
                  onChange({ ...value, [activeDay]: normalized });
                }}
              >
                Z pompą ciepła
              </Button>
            )}

            {hasSolar && (
              <Button
                size="xs"
                variant="light"
                color="yellow"
                onClick={() => {
                  // Solar pattern: reduced daytime (9-17), high morning/evening
                  const pattern = [
                    0.4, 0.4, 0.4, 0.4, 0.5, 0.6,  // 00-05: Night
                    1.5, 1.8, 1.2, 0.3, 0.3, 0.3,  // 06-11: Morning peak, then solar kicks in
                    0.3, 0.3, 0.3, 0.4, 0.8, 1.2,  // 12-17: Midday solar, evening rise
                    1.8, 2.0, 1.8, 1.5, 1.0, 0.6,  // 18-23: Evening peak (no solar)
                  ];
                  const sum = pattern.reduce((a, b) => a + b, 0);
                  const normalized = pattern.map(v => (v / sum) * 100);
                  onChange({ ...value, [activeDay]: normalized });
                }}
              >
                Z panelami solarnymi
              </Button>
            )}

            {hasEV && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                onClick={() => {
                  // EV pattern: high night charging (22-06)
                  const pattern = [
                    2.5, 2.5, 2.5, 2.5, 2.0, 1.5,  // 00-05: Night charging
                    1.0, 1.3, 1.2, 0.8, 0.7, 0.6,  // 06-11: Morning
                    0.6, 0.7, 0.7, 0.8, 0.9, 1.0,  // 12-17: Afternoon
                    1.3, 1.5, 1.4, 1.2, 2.0, 2.5,  // 18-23: Evening + start charging
                  ];
                  const sum = pattern.reduce((a, b) => a + b, 0);
                  const normalized = pattern.map(v => (v / sum) * 100);
                  onChange({ ...value, [activeDay]: normalized });
                }}
              >
                Z samochodem elektrycznym
              </Button>
            )}

            <Button
              size="xs"
              variant="light"
              color="green"
              onClick={() => applySuggestedPattern(activeDay)}
            >
              Inteligentny (analiza z Kroku 2)
            </Button>

            {/* Tauron Import Button - Only show if user is authenticated and has imported data */}
            {isAuthenticated && hasImportedData && (
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconFileImport size={14} />}
                onClick={() => setTauronModalOpened(true)}
                loading={summaryLoading}
              >
                Z raportu Tauron
              </Button>
            )}

            {activeDay === 'weekend' && (
              <Button
                size="xs"
                variant="light"
                onClick={copyWeekdayToWeekend}
              >
                Skopiuj z dni roboczych
              </Button>
            )}
          </Group>
        </Stack>

        {/* Horizontal table with periods as rows and hours as columns */}
        <ScrollArea>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={120}>Okres</Table.Th>
                {/* Generic column headers - hour labels will be shown in each cell */}
                {Array.from({ length: 6 }, (_, i) => (
                  <Table.Th key={i} style={{ textAlign: 'center', minWidth: '90px' }}>
                    Godz. {i + 1}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {PERIODS.map((period) => {
                // Generate array of hours for this period
                const hours = Array.from(
                  { length: period.endHour - period.startHour + 1 },
                  (_unused, i) => period.startHour + i
                );

                return (
                  <Table.Tr key={period.name}>
                    <Table.Td>
                      <Badge
                        color={period.color}
                        variant="light"
                        size="md"
                        fullWidth
                      >
                        {period.name}
                      </Badge>
                    </Table.Td>
                    {hours.map((hour) => {
                      const percentageValue = currentValues[hour].toFixed(1); // Already in percentage (0-100)
                      const kwhValue = calculateHourlyKwh(Number(percentageValue)).toFixed(2);

                      return (
                        <Table.Td key={hour}>
                          <Stack gap={4} align="center">
                            <Text size="xs" fw={600} c="blue">
                              {hour.toString().padStart(2, '0')}:00
                            </Text>
                            <NumberInput
                              size="sm"
                              value={percentageValue}
                              onChange={(val) => {
                                const newValues = [...currentValues];
                                newValues[hour] = Number(val); // Direct value (already in percentage)
                                onChange({
                                  ...value,
                                  [activeDay]: newValues,
                                });
                              }}
                              suffix="%"
                              min={0}
                              max={20}
                              step={0.1}
                              decimalScale={1}
                              hideControls
                              styles={{
                                input: {
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  minWidth: '70px'
                                }
                              }}
                            />
                            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                              {kwhValue} kWh
                            </Text>
                          </Stack>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {/* Monthly Cost Estimates */}
        <Divider my="md" label="Szacowane koszty miesięczne" labelPosition="center" />

        <ScrollArea>
          <Table striped withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: '120px' }}>Miesiąc</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Zużycie (kWh)</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Koszt sprzedaży</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Koszt dystrybucji</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Koszty stałe</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>SUMA</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {monthlyConsumption && monthlyConsumption.length === 12 ? (
                <>
                  {monthlyConsumption.map((consumption, index) => {
                    // Average tariff rates (estimation for G11)
                    const energyRate = 0.50; // PLN/kWh (average energy cost)
                    const distributionRate = 0.30; // PLN/kWh (average distribution cost)
                    const fixedCost = 15; // PLN/month (average fixed cost)

                    // Calculate monthly costs
                    const energyCost = consumption * energyRate;
                    const distributionCost = consumption * distributionRate;
                    const totalCost = energyCost + distributionCost + fixedCost;

                    return (
                      <Table.Tr key={index}>
                        <Table.Td>{MONTH_NAMES_PL[index + 1]}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{consumption.toFixed(0)} kWh</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{energyCost.toFixed(2)} zł</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{distributionCost.toFixed(2)} zł</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{fixedCost.toFixed(2)} zł</Table.Td>
                        <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>{totalCost.toFixed(2)} zł</Table.Td>
                      </Table.Tr>
                    );
                  })}

                  {/* Annual Total Row */}
                  <Table.Tr style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td style={{ fontWeight: 600 }}>SUMA ROCZNA</Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {monthlyConsumption.reduce((sum, val) => sum + val, 0).toFixed(0)} kWh
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {(monthlyConsumption.reduce((sum, val) => sum + val, 0) * 0.50).toFixed(2)} zł
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {(monthlyConsumption.reduce((sum, val) => sum + val, 0) * 0.30).toFixed(2)} zł
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {(15 * 12).toFixed(2)} zł
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {(monthlyConsumption.reduce((sum, val) => sum + val, 0) * 0.80 + 180).toFixed(2)} zł
                    </Table.Td>
                  </Table.Tr>
                </>
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                    <Text size="sm" c="dimmed">
                      Wprowadź miesięczne zużycie energii w Kroku 1, aby zobaczyć szacowane koszty
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        <Paper p="xs" bg="yellow.0" mt="sm">
          <Text size="xs" c="dimmed">
            <strong>Uwaga:</strong> Powyższe koszty są <strong>szacunkowe</strong> i oparte na średnich stawkach taryfy G11.
            Rzeczywiste koszty zostaną obliczone po wybraniu taryf i kliknięciu przycisku "Oblicz i porównaj wszystkie taryfy".
            Stawki orientacyjne: energia ~0.50 zł/kWh, dystrybucja ~0.30 zł/kWh, opłata stała ~15 zł/miesiąc.
          </Text>
        </Paper>

        {/* Summary & Validation */}
        <Group justify="space-between" align="center">
          <Group gap="md" align="center">
            <Text size="sm" c={isValid ? 'dimmed' : 'red'}>
              Suma: {sum.toFixed(1)}%
              {!isValid && (
                <Text component="span" c="red" ml={8}>
                  (Powinna wynosić 100%)
                </Text>
              )}
            </Text>
            {!isValid && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                onClick={normalizeValues}
              >
                Normalizuj
              </Button>
            )}
          </Group>
          <Text size="sm" c="dimmed" fw={600}>
            Suma szacowanego dziennego zużycia energii: {totalKwh.toFixed(2)} kWh
          </Text>
        </Group>
      </Stack>

      {/* Tauron Import Modal */}
      <Modal
        opened={tauronModalOpened}
        onClose={() => {
          setTauronModalOpened(false);
          setSelectedMonth(null);
        }}
        title="Wybierz miesiąc z raportu Tauron"
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconInfoCircle size={18} />} color="blue" variant="light">
            <Text size="sm">
              Wybierz miesiąc, którego wzorzec godzinowy chcesz zastosować w kalkulatorze.
              System automatycznie rozdzieli zużycie na dni robocze i weekendy.
            </Text>
          </Alert>

          {summaryLoading ? (
            <Group justify="center" p="xl">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Ładowanie dostępnych miesięcy...</Text>
            </Group>
          ) : (
            <Select
              label="Miesiąc"
              placeholder="Wybierz miesiąc"
              value={selectedMonth}
              onChange={setSelectedMonth}
              data={
                importSummary?.imports
                  .filter(imp => !imp.isExtrapolated) // Only show imported (not extrapolated) months
                  .map(imp => {
                    const [year, month] = imp.yearMonth.split('-');
                    const monthName = MONTH_NAMES_PL[parseInt(month, 10)];
                    return {
                      value: imp.yearMonth,
                      label: `${monthName} ${year} (${imp.totalConsumption.toFixed(0)} kWh)`,
                    };
                  }) || []
              }
              required
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setTauronModalOpened(false);
                setSelectedMonth(null);
              }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleLoadTauronData}
              loading={loadingTauronData || hourlyDataLoading}
              disabled={!selectedMonth || summaryLoading}
              color="violet"
              leftSection={<IconFileImport size={16} />}
            >
              Załaduj wzorzec
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
